import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useRef, useState } from "react";
import { ThemeProvider, units, useActiveColorScheme } from "@m/alchemy-theme";
import { Editable, Slate } from "slate-react";
import merge from "lodash/merge";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { useUniqueId } from "../../hooks/useUniqueId";
import { CharacterCount } from "../character-count/CharacterCount";
import { ScreenReaderContent } from "../../screen-reader-content/ScreenReaderContent";
import { useClickThrough } from "../../menu-list-item/useClickThrough";
import { focusEditor as focusEditorUtil, getDefaultValue, SLATE_ZERO_WIDTH_SPACE } from "./utilities/index";
import { SlateToolbar } from "./components/Toolbar/SlateToolbar";
import { renderElement } from "./renderElement";
import { renderLeaf } from "./renderLeaf";
import { useEditorWithPlugins } from "./plugins/useEditorWithPlugins";
import * as Styled from "./SlateRTE.styled";
import { useKeyboardEvents } from "./useKeyboardEvents";
import { useSlateRTE } from "./SlateRTEContext";
import { defaults } from "./SlateRTE.defaults";
export const SlateRTE = forwardRef((props, ref) => {
    const { characterLimit, controls = defaults.controls, labelId, "aria-labelledby": ariaLabelledby = labelId, "aria-describedby": ariaDescribedby, maxLength = characterLimit, minHeight, maxHeight, onChange, palette = "interactiveConstructive", onCharacterCount, placeholder = props.stringMap?.placeholderText || "", onMaxLengthStatusChange, onToolbarMenuClose, onToolbarMenuOpen, plugins, required, stringMap: stringMapProp, value, initialValue = value ?? getDefaultValue(), id, ...rest } = props;
    const { stringMap: defaultStringMap } = useSlateRTE(stringMapProp);
    const internalRef = useRef(null);
    const stringMap = merge({}, defaultStringMap, stringMapProp);
    const activeColorScheme = useActiveColorScheme();
    const editor = useEditorWithPlugins();
    const textAreaId = useUniqueId("slate-", id);
    const toolbarLabelId = textAreaId + "toolbar";
    const toolbarRef = useRef(null);
    const editableRef = useRef(null);
    const [hasFocus, setHasFocus] = useState(false);
    const { handleEditorFocus, handleEditorKeyDown } = useKeyboardEvents({
        editor,
        toolbarRef
    });
    const hasCharacterCount = Boolean(maxLength);
    const characterCountId = hasCharacterCount ? textAreaId + "-cc" : undefined;
    const [isTextboxFocused, setIsTextboxFocused] = useState(false);
    const focusTimeoutRef = useRef(undefined);
    const hasFocusRef = useRef(hasFocus);
    hasFocusRef.current = hasFocus;
    const maybeClearFocus = () => {
        window.clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = window.setTimeout(() => {
            if (hasFocusRef.current) {
                setHasFocus(false);
                hasFocusRef.current = false;
            }
        }, 300);
    };
    const keepFocus = () => {
        window.clearTimeout(focusTimeoutRef.current);
        if (!hasFocusRef.current) {
            setHasFocus(true);
            hasFocusRef.current = true;
        }
    };
    const focusEditor = useCallback(() => focusEditorUtil(editor), [editor]);
    // Focus editor on any non-interactive UI click
    const handleClickThrough = useClickThrough(internalRef, focusEditor);
    return (_jsx(ThemeProvider, { colorScheme: activeColorScheme === "alchemyPanel" ? "alchemyLight" : "alchemyPanel", children: _jsx(Styled.SlateRTE, { "data-testid": "SlateRTE", ref: useCombineRefs(internalRef, ref), palette: palette, hasFocus: hasFocus, onClick: handleClickThrough, onMouseEnter: () => {
                keepFocus();
            }, onMouseLeave: () => {
                if (!isTextboxFocused) {
                    maybeClearFocus();
                }
            }, onFocus: (e) => {
                keepFocus();
                if (e.target.dataset.slateEditor === "true") {
                    // focus the editor
                    handleEditorFocus();
                    // This forces a re-render on focus that restores selection
                    setIsTextboxFocused(true);
                }
            }, onBlur: (e) => {
                maybeClearFocus();
                if (e.target.dataset.slateEditor === "true") {
                    setIsTextboxFocused(false);
                }
            }, ...rest, children: _jsxs(Slate, { editor: editor, initialValue: initialValue, onChange: onChange, children: [_jsx(SlateToolbar, { inert: !hasFocus, ref: toolbarRef, "aria-controls": textAreaId, "aria-label": stringMap.toolbarLabelText, "aria-labelledby": [ariaLabelledby || "", toolbarLabelId].join(" "), controls: controls, focusEditor: focusEditor, onToolbarMenuOpen: onToolbarMenuOpen, onToolbarMenuClose: onToolbarMenuClose, stringMap: stringMap, children: stringMap.toolbarLabelText && (_jsx(ScreenReaderContent, { id: toolbarLabelId, "aria-hidden": "true", children: stringMap.toolbarLabelText })) }), _jsx(Styled.EditorWrapper, { ref: editableRef, minHeight: minHeight, maxHeight: maxHeight, shouldCollapseLastMargin: hasCharacterCount, children: _jsx(Editable
                        // https://github.com/ianstormtaylor/slate/pull/5226
                        , { "data-testid": "SlateEditable", scrollSelectionIntoView: process.env.NODE_ENV === "test" ? () => { } : undefined, id: textAreaId, "aria-label": ariaLabelledby ? undefined : stringMap.editableLabelText, "aria-labelledby": ariaLabelledby, "aria-describedby": characterCountId || ariaDescribedby ? `${characterCountId} ${ariaDescribedby}` : "", "aria-roledescription": stringMap.richTextEditor, "aria-placeholder": placeholder, renderElement: renderElement, renderLeaf: renderLeaf, placeholder: placeholder, renderPlaceholder: (props) => {
                                const { style, ...attributes } = props.attributes;
                                // DES-3268
                                delete style.opacity;
                                style.top = `${units(1)}`;
                                return (_jsx(Styled.Placeholder, { "aria-hidden": "true", ...attributes, style: style, children: props.children }));
                            }, "aria-required": props.required, spellCheck: true, style: {
                                outline: "none", // set with :focus-within on the container instead
                                padding: `${units(1)} ${units(2)} ${units(1)}`,
                                ...(minHeight ? { position: "absolute", top: 0, right: 0, bottom: 0, left: 0 } : {})
                            }, onKeyDown: (e) => {
                                handleEditorKeyDown(e);
                            } }) }), hasCharacterCount && (_jsx(CharacterCount, { id: characterCountId, isLive: hasFocus, excludedTextContent: new RegExp(`(${[placeholder, SLATE_ZERO_WIDTH_SPACE].filter(Boolean).join("|")})`, "gm"), elementRef: editableRef, stringMap: stringMap, maxLength: maxLength, onCharacterCount: onCharacterCount, onMaxLengthStatusChange: onMaxLengthStatusChange }))] }) }) }));
});
SlateRTE.defaultProps = {
    controls: defaults.controls
};
SlateRTE.displayName = "SlateRTE";
